/**
 * Module-level tutor Live lifecycle — controls ancillary polling and dashboard refetch.
 * Phases: idle → starting → live → disconnecting → finalizing → idle
 */

export type TutorLiveLifecyclePhase =
  | 'idle'
  | 'starting'
  | 'live'
  | 'disconnecting'
  | 'finalizing';

type Listener = () => void;

let lifecyclePhase: TutorLiveLifecyclePhase = 'idle';
/** Legacy acquire/release ref-count (kept for Strict Mode mount pairs). */
let activeCount = 0;
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getTutorLiveLifecyclePhase(): TutorLiveLifecyclePhase {
  return lifecyclePhase;
}

export function setTutorLiveLifecyclePhase(phase: TutorLiveLifecyclePhase): void {
  if (lifecyclePhase === phase) return;
  lifecyclePhase = phase;
  notify();
}

/** Acquire while a live tutor WebSocket session is in progress (legacy hook). */
export function acquireTutorLiveSession(): () => void {
  activeCount += 1;
  if (activeCount === 1 && lifecyclePhase === 'idle') {
    setTutorLiveLifecyclePhase('live');
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeCount = Math.max(0, activeCount - 1);
    if (activeCount === 0 && lifecyclePhase === 'live') {
      setTutorLiveLifecyclePhase('idle');
    }
  };
}

/** True while Live UI or teardown finalization is in progress. */
export function isTutorLiveSessionActive(): boolean {
  return lifecyclePhase !== 'idle' || activeCount > 0;
}

/** True while dashboard progress/analytics must not network-refetch on invalidation. */
export function isTutorDashboardRefetchSuppressed(): boolean {
  return lifecyclePhase !== 'idle';
}

export function subscribeTutorLiveSessionActive(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function __resetTutorLiveSessionActivityForTests(): void {
  lifecyclePhase = 'idle';
  activeCount = 0;
  listeners.clear();
}
